// Servicio de autenticación - conecta con el Mock API
import { authFetch } from './api.service';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

export interface User {
  id: string;
  email: string;
  nombre: string;
  apellidos: string;
  rol: string;
  companyId: string;
  locationId: string | null;
  locationName: string;
  positionId?: number;
  turno?: string;
  horario?: {
    entrada: string;
    salida: string;
  };
  permissions?: {
    pos?: boolean;
    inventory?: boolean;
    reports?: boolean;
    employees?: boolean;
    cashRegister?: boolean;
    products?: boolean;
    settings?: boolean;
    locations?: string[];
  };
  // Nuevo campo para almacenar los permisos del sistema tal como vienen del backend
  permisos?: Array<{
    id: number;
    code: string;
    name: string;
    module: string;
    menu: string;
    description: string;
  }>;
  // Detalles de locales del usuario (viene del login, evita llamadas adicionales)
  locationsDetails?: Array<{
    id: number;
    name: string;
    companyId: string;
    address?: string;
  }>;
}

export interface Location {
  id: string;
  name: string;
  companyId: string;
  companyName?: string;
  description?: string;
  address?: string;
}

export interface LoginResponse {
  user: User;
  token: string;
  locations: Location[];
}

// Función para obtener las ubicaciones de la empresa
export const getCompanyLocations = async (companyId: string, token: string): Promise<Location[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/companies/${companyId}/locations`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Error al obtener ubicaciones');
    }

    const data = await response.json();
    
    // Mapear los datos del API a la estructura esperada por el frontend
    const locations = (data.data || []).map((location: any) => ({
      id: location.id,
      name: location.nombre, // El API usa 'nombre', el frontend espera 'name'
      companyId: location.companyId,
      companyName: data.meta?.empresa || 'Sangucherías Macartur S.A.C.',
      description: location.direccion, // Usar la dirección como descripción
      address: location.direccion,
    }));
    
    console.log('✅ Locations loaded:', locations.length, 'locations'); // Solo un log limpio
    return locations;
  } catch (error) {
    console.error('Error fetching locations:', error);
    // Retornar ubicaciones mock si falla la API
    return [
      {
        id: 'loc-central',
        name: 'Local Central',
        companyId: companyId,
        companyName: 'Sangucherías Macartur S.A.C.',
        address: 'Av. Javier Prado 123, San Isidro, Lima',
      },
      {
        id: 'loc-miraflores',
        name: 'Sucursal Miraflores',
        companyId: companyId,
        companyName: 'Sangucherías Macartur S.A.C.',
        address: 'Av. Larco 456, Miraflores, Lima',
      },
      {
        id: 'loc-surco',
        name: 'Sucursal Surco',
        companyId: companyId,
        companyName: 'Sangucherías Macartur S.A.C.',
        address: 'Av. Benavides 789, Santiago de Surco, Lima',
      },
      {
        id: 'loc-pueblo-libre',
        name: 'Sucursal Molina',
        companyId: companyId,
        companyName: 'Sangucherías Macartur S.A.C.',
        address: 'Av. Universitaria 321, Pueblo Libre, Lima',
      },
      {
        id: 'loc-fontana',
        name: 'Local Fontana',
        companyId: companyId,
        companyName: 'Sangucherías Macartur S.A.C.',
        address: 'Av. La Fontana 987, La Molina, Lima',
      },
    ];
  }
};

export const login = async (username: string, password: string): Promise<LoginResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: username, // El API espera email, no username
        password: password,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Credenciales inválidas');
    }

    const data = await response.json();
    
    if (!data.success || !data.data) {
      throw new Error('Respuesta inválida del servidor');
    }

    const { user, tokens } = data.data;
    const token = tokens.accessToken;

    // Guardar token inmediatamente para que las llamadas subsiguientes lo tengan
    localStorage.setItem('accessToken', token);

    // Obtener las ubicaciones del empleado
    // Usar locationsDetails del response (evita llamadas adicionales al API)
    let locations: Location[] = [];

    if (user.locationsDetails && Array.isArray(user.locationsDetails) && user.locationsDetails.length > 0) {
      // Usar los detalles que vienen directamente del login response
      locations = user.locationsDetails.map((loc: any) => ({
        id: loc.id.toString(),
        name: loc.name,
        companyId: loc.companyId || user.companyId,
        description: loc.address || '',
        address: loc.address || '',
      }));
      console.log('✅ Using locationsDetails from login response:', locations.length, 'locations');
    } else if (user.locations && Array.isArray(user.locations) && user.locations.length > 0) {
      // Fallback: crear ubicaciones básicas con los IDs (sin llamadas extra)
      locations = user.locations.map((locationId: number) => ({
        id: locationId.toString(),
        name: locationId.toString() === user.locationId?.toString() ? (user.locationName || `Ubicación ${locationId}`) : `Ubicación ${locationId}`,
        companyId: user.companyId,
        description: '',
        address: '',
      }));
      console.log('⚠️ Using basic locations from IDs (no details):', locations.length);
    } else {
      // Fallback final: obtener ubicaciones de la empresa
      locations = await getCompanyLocations(user.companyId, token);
    }

    // Transformar el array de permisos del backend al formato objeto del frontend
    const transformPermissions = (permisos: any[]) => {
      if (!Array.isArray(permisos)) return {};

      const permissions: any = {};
      permisos.forEach(permiso => {
        if (permiso.module) {
          // Mapear los módulos del backend a las propiedades del frontend
          const moduleMap: Record<string, string> = {
            'pos': 'pos',
            'cash_register': 'cashRegister', // Importante: cash_register -> cashRegister
            'inventory': 'inventory',
            'reports': 'reports',
            'employees': 'employees',
            'products': 'products',
            'settings': 'settings'
          };

          const frontendKey = moduleMap[permiso.module];
          if (frontendKey) {
            permissions[frontendKey] = true;
          }
        }
      });

      // También guardar los permisos raw para depuración
      console.log('🔍 Transformed permissions:', {
        raw: permisos,
        transformed: permissions
      });

      return permissions;
    };

    // Adaptar el formato del usuario para mantener compatibilidad
    const adaptedUser: User = {
      id: user.id,
      email: user.email,
      nombre: user.nombre,
      apellidos: user.apellidos,
      rol: user.rol,
      companyId: user.companyId,
      locationId: user.locationId,
      locationName: user.locationName,
      positionId: user.positionId,
      turno: user.turno,
      horario: user.horario,
      permissions: transformPermissions(user.permisos || []),
      permisos: user.permisos || [], // Mantener también la estructura original
      locationsDetails: user.locationsDetails || [], // Detalles de locales para evitar llamadas extra
    };

    return {
      user: adaptedUser,
      token,
      locations,
    };
  } catch (error) {
    console.error('Login error:', error);
    throw error instanceof Error ? error : new Error('Error de autenticación');
  }
};

export const logout = async (): Promise<void> => {
  // Simular delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // En una implementación real, aquí se invalidaría el token en el servidor
  localStorage.removeItem('accessToken');
  localStorage.removeItem('macartur:authToken');
  localStorage.removeItem('macartur:user');
  localStorage.removeItem('macartur:lastSelectedLocation');
};

// Función para obtener información detallada de una ubicación
export const getLocationDetails = async (locationId: string): Promise<any> => {
  try {
    const response = await authFetch(`${API_BASE_URL}/locations/${locationId}`);

    if (!response.ok) {
      throw new Error('Error al obtener detalles de la ubicación');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching location details:', error);
    throw error instanceof Error ? error : new Error('Error al obtener detalles de la ubicación');
  }
};